import { CollectionPopupComponent } from '../../../../shared';
import { voyageSpendErrorMessage } from '../voyage-credits.model';
import { AisMapFeature } from '../../shared/contracts/ais-map.feature';
import { AisMapContext } from '../../shared/contracts/ais-map.context';
import { VoyagePopupActions } from '../vessel-popup.types';
import { vrHost } from '../vr-host';

/**
 * What this feature must be TOLD rather than know (rule R5 / D1 / phase-5
 * boundary). Each callback is a seam to code still living elsewhere:
 */
export interface NearbyFeatureOptions {
  /**
   * The fix for a hull the BROWSER drew, or null — strictly the live-AOI
   * fallback centre (the server prefers its own stored fix). Facade glue
   * because it reads the free search index across snapshot + frames.
   */
  snapshotFixFor: (mmsi: string) => { lat: number; lon: number } | null;
  /**
   * A nearby ROW was clicked — place that hull on the map. Facade glue until
   * the located/lookup machinery finishes its own extraction: the pick spans
   * the located store, the selection guard and the paid lookup.
   */
  pickRowVessel: (mmsi: string, onSettled?: () => void) => void;
  /** Re-push every price line after a wallet-moving response (the facade's broadcast). */
  syncCreditLines: () => void;
}

/**
 * NEARBY VESSELS — the by-vessel proximity search drawer's map-side half, as
 * a pluggable feature (phase 5, REFACTOR-PLAN.md §4.3/§7). Extracted verbatim
 * from `VoyageMapController`. Line for line the same shape as the port-calls
 * feature — same guards, same wallet mirror, same failure copy — because it
 * is the same kind of act: a paid per-MMSI question whose answer is stored
 * inline on the sensor so re-opening the drawer costs nothing. That sameness
 * is the whole reason phase 5 exists (§7: "proves the contract on code that
 * is already triplicated").
 *
 * The provider API call inside (`getVoyageRadarNearbyVessels`) is the
 * feature's gateway seam — extracted to `vr-nearby.gateway.ts` when a second
 * provider lands (§5.11).
 */
export class NearbyFeature extends AisMapFeature {
  /**
   * MMSI of the nearby-vessel search in flight, or null. Its own field for the
   * same reason the port-call guard is: three separate purchases sit on
   * this card, and one being out must not stand the others down — but a
   * double-click on any of them must never buy the same answer twice.
   *
   * It matters more here than it does there, because a nearby search is the one
   * of the three whose result the user can change without changing the hull:
   * two clicks at two radii are two different questions and two legitimate
   * charges, so nothing further down can tell a duplicate from a re-ask. This
   * guard is what makes the FIRST of two identical clicks the only one that
   * spends.
   */
  private nearbyLoadingMmsi: string | null = null;

  constructor(ctx: AisMapContext, private readonly opts: NearbyFeatureOptions) {
    super(ctx);
  }

  /** What one nearby search costs on this provider, in its own units. */
  private price(): number {
    return this.ctx.spend?.priceFor('nearby') ?? 0;
  }

  /**
   * Nearby vessels — the same three callbacks as port calls, wired the same
   * way and for the same reasons, because the drawer is a copy of that one.
   * The free half is `onLookupStored` again: `nearbyHistory` rides to the
   * client on the sensor document exactly as `portCallHistory` does, so
   * re-opening a bought list is a lookup in memory rather than a second
   * 2-credit question.
   *
   * `onFetch` takes a THIRD argument the port-call one has no use for. The
   * radius is the user's own, it is part of what was bought, and it lives in
   * the controller's state rather than in a DOM input — so the drawer's
   * header action and the popup's radar icon reach this call the same way,
   * carrying the same number.
   *
   * The fourth wire is the one port calls has no equivalent of: a row click
   * PLACES A HULL. `/v1/vessels/nearby` returns no position for anything in
   * its list, so a row is a name and a distance until someone buys its fix —
   * and the drawer has neither a map nor a wallet, where the map side has
   * both. The remembered radius is pushed by `syncCredits` for a related
   * reason: it is scoped by task AND sensor, and a toolbar control is handed
   * `{ controller, slot, options }` and so cannot know either.
   */
  override attach(): void {
    const nearby = vrHost(this.ctx.host).nearby;
    nearby.onLookupStored = (mmsi) =>
      (this.ctx.sensor?.nearbyHistory || []).find((r: any) => String(r?.mmsi) === mmsi) ??
      null;
    nearby.onFetch = (mmsi, vesselName, radiusKm) =>
      this.fetchNearbyVessels(mmsi, vesselName, radiusKm);
    nearby.onPickVessel = (mmsi, onSettled) => this.opts.pickRowVessel(mmsi, onSettled);
  }

  override syncCredits(remaining: number | null, spendingClosed: boolean): void {
    const nearby = vrHost(this.ctx.host).nearby;
    // Two prices: the search on the drawer's own button, and the ordinary
    // vessel lookup a row spends when it is clicked.
    nearby.setCredits(
      remaining,
      this.price(),
      this.ctx.spend?.priceFor('lookup') ?? 0,
      spendingClosed,
    );
    // The remembered radius is scoped by task AND sensor — the two facts
    // travel with the balance so neither goes stale alone (invariant I6).
    nearby.setStorageScope(
      this.ctx.taskId,
      this.ctx.sensor?.sensor_uuid ?? this.ctx.sensor?.id ?? null,
    );
  }

  override popupActions(mmsi: string): Partial<VoyagePopupActions> {
    return {
      /**
       * The other half of the question the anchor beside it answers: port
       * calls say where this hull has BEEN, nearby says what is WITH it right
       * now. Offered on the same terms — any arrow on the chart is a
       * legitimate centre — and gated the same way: an unmounted drawer
       * contributes null, which hides the button (invariant I5).
       */
      nearbyCredits: vrHost(this.ctx.host).nearby.onRequestOpen ? this.price() : null,
    };
  }

  /**
   * The by-vessel nearby search: who is around THIS hull, right now.
   *
   * Three things differ from the port-call twin, and all three come from the
   * endpoint rather than taste:
   *
   *  - IT CARRIES A RADIUS, which the operator chose. It is sent, and the radius
   *    the server actually SERVED comes back on the record — those differ
   *    whenever the platform clamped, and the drawer labels itself with the
   *    second, never the first.
   *  - IT SENDS A CENTRE ONLY WHEN IT HAS A REAL ONE. The server prefers its own
   *    stored fix for the hull and treats anything non-finite or out of range as
   *    absent, so `lat`/`lon` here are strictly the live-AOI case: a hull drawn
   *    from a snapshot that was never written to Mongo. `api.service` drops them
   *    when they are not real numbers, so passing undefined is honest rather
   *    than lossy.
   *  - AN EMPTY ANSWER IS STILL AN ANSWER. Nothing within 5 km of a ship in open
   *    water is the correct reply to a question the user framed, not a failure,
   *    and it was free. Handing a count-0 record to the controller is what keeps
   *    the drawer from dropping back to "not searched" and re-offering a spend
   *    that already happened.
   */
  fetchNearbyVessels(mmsi: string, vesselName: string | null, radiusKm: number): boolean {
    const sensor = this.ctx.sensor;
    const taskid = this.ctx.host.collection?.taskId;
    const sensorId = sensor?.sensor_uuid ?? sensor?.id;
    const nearby = vrHost(this.ctx.host).nearby;
    if (!taskid || !sensorId || !mmsi) {
      nearby.setFailed();
      return false;
    }
    // In-flight guard — a double click must not buy the same search twice.
    if (this.nearbyLoadingMmsi) return false;

    const refusal = this.ctx.spend ? this.ctx.spend.refuse('nearby', this.price()) : null;
    if (refusal) {
      nearby.setFailed();
      this.ctx.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return false;
    }

    // The live-AOI centre, when this hull is one the browser drew and Mongo has
    // never seen. Anything else and the server's own stored fix is both better
    // and already there.
    const centre = this.opts.snapshotFixFor(mmsi);

    this.nearbyLoadingMmsi = mmsi;
    this.ctx.host.apiService
      .getVoyageRadarNearbyVessels({
        taskid,
        sensorId,
        mmsi,
        radiusKm,
        lat: centre?.lat,
        lon: centre?.lon,
        vesselName,
      })
      .toPromise()
      .then((res: any) => {
        this.nearbyLoadingMmsi = null;
        if (res?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = res.apiCreditsRemaining;
          this.opts.syncCreditLines();
        }
        if (!res?.record) {
          // No record came back, which the route does on the free paths. The
          // question was still answered, so it is shown as one — carrying the
          // radius so the drawer can say WHAT was empty rather than just that
          // something was.
          nearby.setFetched(mmsi, {
            mmsi,
            vessels: [],
            count: 0,
            truncated: false,
            radiusKm: res?.radiusKm ?? radiusKm,
            name: null,
          });
          return;
        }
        // Mirror the server's record onto the local sensor doc, same shape and
        // same cap, so the drawer and the panel agree without a refetch.
        const list: any[] = Array.isArray(sensor.nearbyHistory) ? sensor.nearbyHistory : [];
        const i = list.findIndex((r: any) => String(r?.mmsi) === mmsi);
        if (i >= 0) list[i] = res.record;
        else {
          list.push(res.record);
          if (list.length > 25) list.splice(0, list.length - 25);
        }
        sensor.nearbyHistory = list;
        nearby.setFetched(mmsi, res.record);
      })
      .catch((err: any) => {
        this.nearbyLoadingMmsi = null;
        nearby.setFailed();
        if (err?.error?.apiCreditsRemaining != null && sensor) {
          sensor.apiCreditsRemaining = err.error.apiCreditsRemaining;
          this.opts.syncCreditLines();
        }
        // A 409 is not a failure of the platform but a missing precondition, and
        // it has its own way out: buy the 1-credit lookup that gives this hull a
        // stored position permanently. Saying so is the difference between a
        // dead end and a next step. Everything else goes through the shared
        // ladder, which always states the charge outcome.
        const message =
          err?.status === 409 || err?.error?.code === 'NO_POSITION'
            ? 'This vessel has no stored position yet, so there is no centre to search around. ' +
              'Use Get latest on it first — that is 1 credit and it fixes this for good. ' +
              'You were not charged.'
            : voyageSpendErrorMessage(err, 'nearby', this.price());
        this.ctx.host.popupService.openPopup(CollectionPopupComponent, message, '', '40%');
      });
    return true;
  }
}
